// Compliance wording: labels are copied verbatim from the design canvas; do not reword.

import * as Sentry from '@sentry/react';
import type {
  ChangeStatusEnum,
  ContactMethodEnum,
  FollowUpEnum,
  MemberResponseEnum,
  NotReachedReasonEnum,
  ReasonEnum,
  ServiceCodeEnum,
  StatusNeedEnum,
  T1016ActivityEnum,
} from 'api/v2';
import {humanizeSnakeCase} from 'utils/humanizeSnakeCase';
import type {Choice} from '../NoteForm';

export type {Choice};
export {OTHER} from '../NoteForm';

// Object.entries puts number-like keys (e.g. '12') first, so enums keep string keys
const choicesFor = <K extends string>(labels: Record<K, string>): Choice<K>[] =>
  Object.entries<string>(labels).map(([key, label]) => ({
    key: key as K,
    label,
  }));

export const CONTACT_METHODS = choicesFor<ContactMethodEnum>({
  attempted_not_reached: 'Attempted - not reached',
  email_other: 'Email/other',
  face_to_face: 'Face-to-face',
  pathfinder_coordination: 'Pathfinder coordination',
  provider_team_coordination: 'Provider/team coordination',
  secure_message_text: 'Secure message/text',
  telehealth: 'Telehealth',
  telephone: 'Telephone',
  other: 'Other',
});

export const SERVICE_CODES = choicesFor<ServiceCodeEnum>({
  H0023: 'H0023 · Behavioral health outreach',
  T1016: 'T1016 · Case management',
});

export const SERVICE_CODE_DESCRIPTIONS: Record<ServiceCode, string> = {
  H0023:
    'Targeted outreach to engage or reconnect an at-risk member with needed behavioral-health treatment, recovery supports, or services.',
  T1016:
    'Case-management/care-coordination activity addressing an identified client need and helping coordinate, access, or maintain services.',
};

export const SERVICE_ACTIVITIES = choicesFor<T1016ActivityEnum>({
  assessed_need: 'Assessed service/care-coordination need',
  assisted_access: 'Assisted client with accessing treatment/services',
  coordinated_care: 'Coordinated care with treatment team/provider',
  coordinated_referral: 'Coordinated referral or connection to another service',
  addressed_barrier: 'Addressed barrier affecting access/continuity of care',
  coordinated_resources: 'Coordinated behavioral-health/recovery resources',
  supported_transition: 'Supported transition between levels/providers of care',
  followed_up_care_plan:
    'Followed up regarding established care plan/service need',
  communicated_with_provider:
    'Communicated with another provider regarding client care',
  other: 'Other',
});

export const REASONS = choicesFor<ReasonEnum>({
  concerning_check_in: 'Concerning check-in',
  concerning_feelings: 'Concerning feelings',
  missed_participation: 'Missed or decreased treatment participation',
  change_in_recovery_status: 'Change in self-reported recovery status',
  potential_concern: 'Potential recovery/behavioral-health concern',
  needs_additional_support: 'Client identified as needing additional support',
  existing_concern_follow_up: 'Follow-up regarding an existing concern',
  coordination_of_services: 'Coordination of behavioral-health services',
  barrier_to_services: 'Barrier to accessing or continuing services',
  post_crisis: 'Post-crisis or transition follow-up',
  routine_outreach: 'Routine planned outreach',
  other: 'Other',
});

export const CHANGE_STATUSES = choicesFor<ChangeStatusEnum>({
  existing_concern_continues: 'Existing concern continues',
  improvement: 'Improvement',
  new_concern: 'New concern identified',
  no_new_concern: 'No new concern',
  no_significant_change: 'No significant change',
  unable_to_determine: 'Unable to determine',
});

export const STATUS_NEEDS = choicesFor<StatusNeedEnum>({
  decreased_engagement: 'Decreased engagement or participation',
  reported_challenge: 'Client reported a BH/recovery challenge',
  increased_need: 'Client reported increased need for support',
  barrier_to_treatment: 'Barrier to treatment/services',
  care_coordination_need: 'Care coordination need',
  other: 'Other',
});

export const RESPONSES = choicesFor<MemberResponseEnum>({
  engaged_receptive: 'Engaged and receptive',
  identified_needs: 'Identified additional needs/concerns',
  agreed_to_continue: 'Agreed to reconnect/continue treatment',
  agreed_to_follow_up: 'Agreed to follow-up',
  utilizing_supports: 'Reports utilizing available supports',
  barrier_addressed: 'Barrier addressed/resolved',
  referral_initiated: 'Referral/resource connection initiated',
  declined_support: 'Declined additional support',
  no_direct_contact: 'No direct client contact occurred',
  non_committal: 'Client is non-committal to intervention',
  other: 'Other',
});

export const FOLLOW_UPS = choicesFor<FollowUpEnum>({
  continue_routine_support: 'Continue routine support',
  follow_up_with_client: 'Follow up with client',
  coordinate_with_team: 'Coordinate with treatment team/provider',
  connect_to_resource: 'Connect client to resource/service',
  follow_up_on_referral: 'Follow up on referral',
  encourage_engagement: 'Encourage continued engagement in treatment',
  review_next_contact: 'Review at next scheduled contact',
  escalate: 'Escalate per organizational protocol',
  no_action: 'No additional action indicated',
  other: 'Other',
});

export const NOT_REACHED_REASONS = choicesFor<NotReachedReasonEnum>({
  concerning_check_in: 'Concerning check-in',
  concerning_feelings: 'Concerning feelings',
  missed_participation: 'Missed or decreased treatment participation',
  existing_concern_follow_up: 'Follow-up regarding an existing concern',
  routine_outreach: 'Routine planned outreach',
  post_crisis: 'Post-crisis or transition follow-up',
  other: 'Other',
});

export type ContactMethod = ContactMethodEnum;
export type ServiceCode = ServiceCodeEnum;
export type ChangeStatus = ChangeStatusEnum;
export type NotReachedReason = NotReachedReasonEnum;

export const NOT_REACHED: ContactMethod = 'attempted_not_reached';
export const FOLLOW_UP_WITH_CLIENT: FollowUpEnum = 'follow_up_with_client';
export const NO_ACTION: FollowUpEnum = 'no_action';
export const OTHER_PLACE_OF_SERVICE = '99';
export const NO_DIRECT_CONTACT: MemberResponseEnum = 'no_direct_contact';

const reportedKeys = new Set<string>();

// An open tab can outlive a deploy that adds an option, so fall back to a readable key
export const labelOf = (list: readonly Choice[], key: string | null) => {
  if (!key) {
    return '';
  }
  const label = list.find((choice) => choice.key === key)?.label;
  if (label !== undefined) {
    return label;
  }
  if (!reportedKeys.has(key)) {
    reportedKeys.add(key);
    Sentry.captureMessage(`No label for option "${key}"`);
  }
  return humanizeSnakeCase(key);
};
